import React from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Home, Store, PlusCircle, MessageSquare, User, ShieldCheck, LayoutDashboard, LogOut, Plus, Heart, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/components/common";
import { MtaaWordmark } from "@/components/MtaaLogo";
import { CreateMenu } from "@/components/community";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Button } from "@/components/ui/button";

const NAV = [
  { id: "home", label: "Home", icon: Home, to: "/app" },
  { id: "market", label: "Market", icon: Store, to: "/explore" },
  { id: "deals", label: "Deals", icon: Tag, to: "/deals", desktopOnly: true },
  { id: "post", label: "Post", icon: PlusCircle, to: "/post", primary: true },
  { id: "messages", label: "Messages", icon: MessageSquare, to: "/messages" },
  { id: "profile", label: "Profile", icon: User, to: "/profile" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const active = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop header */}
      <header className="glass sticky top-0 z-40 hidden border-b border-border md:block">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
          <Link to="/app" className="flex items-center gap-2" data-testid="logo-desktop">
            <MtaaWordmark />
          </Link>
          <nav className="flex items-center gap-1">
            {NAV.filter((n) => !n.primary).map((n) => (
              <Link key={n.id} to={n.to} data-testid={`nav-desktop-${n.id}`}
                className={cn("rounded-lg px-3 py-2 text-sm font-medium transition-colors", active(n.to) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground")}>
                {n.label}
              </Link>
            ))}
            <Link to="/safety" className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="nav-desktop-safety">Safety</Link>
            <Link to="/support" className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-brand-red" data-testid="nav-desktop-support">Support</Link>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <CreateMenu><Button size="sm" data-testid="header-post-btn"><PlusCircle className="h-4 w-4" />Create</Button></CreateMenu>
            {user ? (
              <>
                {["owner", "admin", "moderator"].includes(user.role) && (
                  <Link to="/admin"><Button size="sm" variant="outline" data-testid="header-admin-btn"><LayoutDashboard className="h-4 w-4" />Admin</Button></Link>
                )}
                <NotificationsBell variant="desktop" />
                <Link to="/profile" data-testid="header-avatar">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary font-semibold text-primary">{(user.name || "U")[0].toUpperCase()}</div>
                </Link>
                <Button size="sm" variant="ghost" onClick={() => logout().then(() => nav("/"))} data-testid="header-logout"><LogOut className="h-4 w-4" /></Button>
              </>
            ) : (
              <Link to="/login"><Button size="sm" variant="outline" data-testid="header-login-btn">Sign in</Button></Link>
            )}
          </div>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className="glass sticky top-0 z-40 flex items-center justify-between border-b border-border px-4 py-3 md:hidden">
        <Link to="/app" data-testid="logo-mobile">
          <MtaaWordmark compact />
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/safety" className="rounded-lg p-2" data-testid="mobile-safety"><ShieldCheck className="h-5 w-5 text-primary" /></Link>
          <Link to="/support" className="rounded-lg p-2" data-testid="mobile-support"><Heart className="h-5 w-5 text-brand-red" /></Link>
          <NotificationsBell variant="mobile" />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-28 pt-4 md:px-6 md:pb-12">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around border-t border-border bg-background/95 px-2 pb-[env(safe-area-inset-bottom)] pt-1.5 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] backdrop-blur-lg md:hidden" data-testid="bottom-nav">
        {NAV.filter((n) => !n.desktopOnly).map((n) => {
          const Icon = n.icon;
          if (n.primary) return (
            <div key={n.id} className="flex flex-col items-center">
              <CreateMenu>
                <button data-testid={`nav-${n.id}`} className="-mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-brand-red text-white shadow-lg shadow-brand-red/30 ring-4 ring-background transition-transform active:scale-95">
                  <Plus className="h-7 w-7" />
                </button>
              </CreateMenu>
            </div>
          );
          return (
            <Link key={n.id} to={n.to} data-testid={`nav-${n.id}`}
              className={cn("flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[11px] font-medium transition-colors", active(n.to) ? "text-primary" : "text-muted-foreground")}>
              <Icon className="h-5 w-5" />{n.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
